/* eslint-disable max-len */
import React, { useEffect, useState, useMemo } from 'react';
import {
  extend
} from '@thd-nucleus/data-sources';
import { ListSettings } from '@thd-olt-component-react/list-forms';
import { useImpression } from '@thd-olt-component-react/impression';
import {
  func, shape, string, arrayOf, number, bool
} from 'prop-types';
import { ArrowForward } from '@one-thd/sui-icons';
import {
  Card, CardBody, CardTitle, Tooltip, Link, Typography
} from '@one-thd/sui-atomic-components';
import { Thumbnails } from './Thumbnails';
import { ListTilePlaceholder } from './ListTilePlaceholder';
import { ListTitle } from './ListTitle';

export const ListTile = ({
  listId,
  data,
  onDelete,
  position,
  iconSize,
  disableLink = false,
  disableSettings = false,
  numberOfThumbnails = 3
}) => {

  const list = data;

  const [shortListName, setShortListName] = useState('');
  const listName = list?.name ?? '';

  const isEmptyList = list?.itemCount === 0;
  const listNameIsLong = listName && listName.length > 20;

  const listNameSpliced = useMemo(() => {
    const cutThreeTimesListName = listName && listName.length > 40;
    const firstPartListName = listName.slice(0, 20);
    const secondPartListName = listName.slice(20, 40);
    const thirdPartListName = listName.slice(40, listName.length);
    return cutThreeTimesListName
      ? `${firstPartListName} ${secondPartListName} ${thirdPartListName}`
      : `${firstPartListName} ${secondPartListName}`;
  }, [listName]);

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('list-tile.ready');
  }, []);

  useEffect(() => {
    if (listNameIsLong) {
      const listNameSubStr = list?.name.slice(0, 14);
      const newListNameWithEllipsis = listNameSubStr.concat('...');

      setShortListName(newListNameWithEllipsis);
    }
  }, [listNameIsLong]);

  const onDeleteConfirmation = () => {
    if (onDelete) {
      onDelete({ listId });
    }
  };

  const impressionContextData = useImpression({
    data: {
      id: listId,
      name: 'ListTile',
      component: 'ListTile',
      position: position + 1,
      type: 'content'
    }
  });

  if (!list) return <ListTilePlaceholder />;

  if (!list || !list?.url) return null;

  const itemCountMessage = `${list?.itemCount || 0} ${list?.itemCount === 1 ? ' item' : ' items'}`;

  const listTileRef = impressionContextData.ref;
  const listTileClickId = impressionContextData.clickID;

  const ListTitleElement = (
    <ListTitle
      disableLink={disableLink}
      url={list?.url}
      listNameIsLong={listNameIsLong}
      shortListName={shortListName}
      listName={list?.name}
    />
  );

  const ListTitleTooltip = (
    <Tooltip
      placement="top"
      title={listNameSpliced}
    >
      {ListTitleElement}
    </Tooltip>
  );

  const actionBtn = (
    <>
      {
        !disableSettings && (
          <ListSettings
            iconSize={iconSize}
            onClose={() => { }}
            list={list}
            onDelete={onDeleteConfirmation}
            isEmptyList={isEmptyList}
          />
        )
      }
      {
        disableLink && (
          <ArrowForward size="small" />
        )
      }
    </>
  );

  const subHeader = (
    <>
      {
        (!disableLink) ? (
          <Link href={list?.url} className="sui-font-regular sui-text-base">
            {itemCountMessage}
          </Link>
        ) : (<>{itemCountMessage}</>)
      }
    </>
  );

  const listTileHeader = (listNameIsLong) ? ListTitleTooltip : ListTitleElement;
  return (
    <div
      className="sui-w-full"
      ref={listTileRef}
      // eslint-disable-next-line react/no-unknown-property
      clickid={listTileClickId}
      data-component="ListTile"
      data-testid="list-tile"
    >
      <Card
        className="sui-grid sui-rounded-md sui-shadow-base sui-h-full"
      >
        <CardTitle
          header={(listTileHeader)}
          subheader={(subHeader)}
          action={(actionBtn)}
        />
        <CardBody>
          <Thumbnails
            numberOfThumbnails={numberOfThumbnails}
            url={disableLink ? '' : list?.url}
            thumbnails={list?.images}
          />
        </CardBody>
      </Card>
    </div>
  );
};

ListTile.displayName = 'ListTile';

ListTile.defaultProps = {
  onDelete: null,
  data: null,
  position: 0,
  iconSize: 'small',
  disableLink: false,
  disableSettings: false,
  numberOfThumbnails: 3
};

ListTile.propTypes = {
  listId: string.isRequired,
  onDelete: func,
  iconSize: string,
  data: shape({
    id: string,
    name: string,
    url: string,
    itemCount: number,
    images: arrayOf(shape({
      url: string
    }))
  }),
  position: number,
  disableLink: bool,
  disableSettings: bool,
  numberOfThumbnails: number
};

ListTile.dataModel = extend(ListSettings);
